import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { CompanyComplianceType } from 'src/app/classes/models/company-compliance-type';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';

import { GetUserInputModalComponent } from '../get-user-input-modal/get-user-input-modal.component';

@Component({
  selector: 'app-configure-compliance-types-modal',
  templateUrl: './configure-compliance-types-modal.component.html',
  styleUrls: ['./configure-compliance-types-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ConfigureComplianceTypesModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ModalService: NgbModal,
    private AuthService: AuthService,
    private CompanyService: CompanyService,
    private ToastService: ToastService,
    private HostElement: ElementRef,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  @Input() CompanyId?: number;
  @Input() companyComplianceTypes?: CompanyComplianceType[];

  nonEditableComplianceTypes: string[] = ['default', 'transporter'];

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  closeModal(result?: any) {
    this.activeModal.close(result);
  }

  deleteType(companyComplianceType: CompanyComplianceType) {
    if (!companyComplianceType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot delete company compliance type. No company compliance type found.',
        { returnError: true }
      );
    }

    const { id } = companyComplianceType;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot delete company compliance type. No company compliance type ID found.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Deleting compliance type...'
    });

    this.CompanyService.deleteCompanyComplianceType(id).subscribe({
      next: (result) => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully deleted company compliance type.'
        );
        const companyComplianceTypeToBeDeleted =
          this.companyComplianceTypes?.findIndex((type) => type.id === id);

        if (companyComplianceTypeToBeDeleted) {
          this.companyComplianceTypes?.splice(
            companyComplianceTypeToBeDeleted,
            1
          );
        }
        mask.destroy();
        this.ChangeDetectorRef.markForCheck();
      },
      error: () => mask.destroy()
    });
  }

  editType(companyComplianceType: CompanyComplianceType) {
    if (!companyComplianceType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot edit company compliance type. No company compliance type found.',
        { returnError: true }
      );
    }

    const { type } = companyComplianceType;

    if (!type) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot edit company compliance type. No company compliance type found.',
        { returnError: true }
      );
    }

    const modalRef = this.ModalService.open(GetUserInputModalComponent, {
      size: 'md'
    });
    modalRef.componentInstance.title = 'Edit Company Compliance Type';
    modalRef.componentInstance.message = 'Please update type:';
    modalRef.componentInstance.inputValue = type;

    modalRef.closed.subscribe({
      next: (updatedType) => {
        if (updatedType) {
          Object.assign(companyComplianceType, { type: updatedType });

          const { nativeElement } = this.HostElement;
          const mask = new LoadingMask(nativeElement, {
            maskText: 'Editing compliance type...'
          });

          this.CompanyService.saveCompanyComplianceType(
            companyComplianceType
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully updated Company Compliance Type.'
              );
              mask.destroy();
              this.ChangeDetectorRef.markForCheck();
            },
            error: () => mask.destroy()
          });
        }
      }
    });
  }

  addNewType() {
    const modalRef = this.ModalService.open(GetUserInputModalComponent, {
      size: 'md'
    });
    modalRef.componentInstance.title = 'Add Company Compliance Type';
    modalRef.componentInstance.message = 'Please add type:';

    modalRef.closed.subscribe({
      next: (type) => {
        if (type) {
          const newCompanyComplianceType: CompanyComplianceType = {
            CompanyId: this.CompanyId,
            type
          };

          const { nativeElement } = this.HostElement;
          const mask = new LoadingMask(nativeElement, {
            maskText: 'Adding new compliance type...'
          });

          this.CompanyService.saveCompanyComplianceType(
            newCompanyComplianceType
          ).subscribe({
            next: (companyComplianceType: CompanyComplianceType) => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully added Company Compliance Type.'
              );
              this.companyComplianceTypes?.push(companyComplianceType);
              mask.destroy();
              this.ChangeDetectorRef.markForCheck();
            },
            error: () => mask.destroy()
          });
        }
      }
    });
  }

  loadCompanyComplianceTypes() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot display compliances types. Company ID is missing.',
        { returnError: true }
      );
    }
    this.CompanyService.getCompanyComplianceTypes({
      CompanyId: this.CompanyId
    }).subscribe({
      next: (CompanyComplianceTypes: CompanyComplianceType[]) => {
        if (CompanyComplianceTypes && CompanyComplianceTypes.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot display compliance types. Compliance Types missing.',
            { returnError: true }
          );
        }

        this.companyComplianceTypes = CompanyComplianceTypes;
      }
    });
  }

  ngOnInit(): void {
    if (
      !this.companyComplianceTypes ||
      this.companyComplianceTypes.length === 0
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot display compliance types. Compliance Types missing.',
        { returnError: true }
      );
    }
  }
}
